// ============================================================================
// RoleSelection — Schermata iniziale con scelta Dipendente / Datore
// ============================================================================

import { useState } from "react";
import { HardHat, Briefcase, ArrowRight } from "lucide-react";

const roles = [
  {
    id: "employee",
    label: "Dipendente",
    desc: "Accedi per inserire le ore lavorate sul cantiere",
    icon: HardHat,
    color: "#C0392B",
  },
  {
    id: "admin",
    label: "Datore di Lavoro",
    desc: "Pannello completo: ore, dipendenti, cantieri, report",
    icon: Briefcase,
    color: "#C0392B",
  },
];

export default function RoleSelection({ onSelect }) {
  const [hovered, setHovered] = useState(null);

  return (
    <div style={{
      minHeight: "100vh",
      background: "#000",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: 24,
      position: "relative",
      overflow: "hidden",
    }}>

      {/* ===== Mesh gradient animato — aurora rossa ===== */}
      <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
        <div style={{
          position: "absolute", top: "-20%", left: "-15%",
          width: 700, height: 700,
          borderRadius: "40% 60% 70% 30% / 40% 50% 60% 50%",
          background: "rgba(192,57,43,0.50)",
          filter: "blur(120px)",
          animation: "orbMove1 10s ease-in-out infinite",
        }} />
        <div style={{
          position: "absolute", bottom: "-15%", right: "-10%",
          width: 600, height: 600,
          borderRadius: "60% 40% 30% 70% / 50% 60% 40% 50%",
          background: "rgba(150,40,27,0.45)",
          filter: "blur(100px)",
          animation: "orbMove2 12s ease-in-out infinite",
        }} />
        <div style={{
          position: "absolute", top: "20%", left: "30%",
          width: 500, height: 500,
          borderRadius: "50% 50% 40% 60% / 60% 40% 50% 50%",
          background: "rgba(231,76,60,0.35)",
          filter: "blur(110px)",
          animation: "orbMove3 14s ease-in-out infinite",
        }} />
        <div style={{
          position: "absolute", top: "55%", right: "20%",
          width: 400, height: 400,
          borderRadius: "50%",
          background: "rgba(192,57,43,0.40)",
          filter: "blur(90px)",
          animation: "orbMove4 8s ease-in-out infinite",
        }} />
        {/* Angolo alto destra */}
        <div style={{
          position: "absolute", top: "-40px", right: "-40px",
          width: 200, height: 200,
          borderRadius: "50%",
          background: "rgba(192,57,43,0.45)",
          filter: "blur(60px)",
        }} />
        {/* Angolo basso sinistra */}
        <div style={{
          position: "absolute", bottom: "-40px", left: "-40px",
          width: 200, height: 200,
          borderRadius: "50%",
          background: "rgba(150,40,27,0.45)",
          filter: "blur(60px)",
        }} />
      </div>

      {/* Logo */}
      <div className="animate-fade-in" style={{ marginBottom: 12, position: "relative", zIndex: 1 }}>
        <svg width="240" height="130" viewBox="0 0 220 120" xmlns="http://www.w3.org/2000/svg">
          <line x1="80" y1="6" x2="214" y2="6" stroke="rgba(255,255,255,0.5)" strokeWidth="2"/>
          <line x1="214" y1="6" x2="214" y2="114" stroke="rgba(255,255,255,0.5)" strokeWidth="2"/>
          <line x1="80" y1="114" x2="214" y2="114" stroke="rgba(255,255,255,0.5)" strokeWidth="2"/>
          <line x1="80" y1="6" x2="80" y2="63" stroke="rgba(255,255,255,0.5)" strokeWidth="2"/>
          <line x1="80" y1="90" x2="80" y2="114" stroke="rgba(255,255,255,0.5)" strokeWidth="2"/>
          <text x="4" y="88" fontFamily="'DM Sans','Helvetica Neue',Arial,sans-serif" fontSize="26" fontWeight="400" fill="#C0392B" letterSpacing="0.5">Mattone Rosso</text>
          <text x="5" y="107" fontFamily="'DM Sans','Helvetica Neue',Arial,sans-serif" fontSize="9" fontWeight="400" fill="rgba(192,57,43,0.5)" letterSpacing="5">SRL</text>
        </svg>
      </div>

      <p className="animate-fade-in" style={{
        color: "rgba(255,255,255,0.25)", fontSize: 12,
        margin: "0 0 48px", textAlign: "center",
        letterSpacing: "4px", textTransform: "uppercase", fontWeight: 500,
        position: "relative", zIndex: 1,
      }}>
        Gestione Ore e Cantieri
      </p>

      {/* Role cards */}
      <div style={{
        display: "flex", gap: 28, flexWrap: "wrap",
        justifyContent: "center", width: "100%", maxWidth: 720,
        position: "relative", zIndex: 1,
      }}>
        {roles.map((role, i) => {
          const isHovered = hovered === role.id;
          const Icon = role.icon;
          return (
            <button
              key={role.id}
              onClick={() => onSelect(role.id)}
              onMouseEnter={() => setHovered(role.id)}
              onMouseLeave={() => setHovered(null)}
              className="animate-slide-up"
              style={{
                flex: "1 1 280px", maxWidth: 330,
                background: "#0c0c10",
                border: "none",
                borderRadius: 28,
                padding: "48px 36px 44px",
                cursor: "pointer",
                transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
                transform: isHovered ? "translateY(-10px) scale(1.02)" : "translateY(0) scale(1)",
                boxShadow: isHovered
                  ? "0 0 50px rgba(192,57,43,0.3), 0 0 100px rgba(192,57,43,0.15), 0 20px 60px rgba(0,0,0,0.5), inset 0 0 0 2px rgba(192,57,43,0.6)"
                  : "0 0 20px rgba(192,57,43,0.05), 0 8px 30px rgba(0,0,0,0.4), inset 0 0 0 1px rgba(192,57,43,0.1)",
                display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center",
                animationDelay: `${i * 0.15}s`,
                fontFamily: "var(--font)",
                animation: isHovered ? "none" : "cardGlow 4s ease-in-out infinite",
              }}
            >
              {/* Icona con glow */}
              <div style={{
                width: 80, height: 80, borderRadius: 22,
                background: "rgba(192,57,43,0.06)",
                border: `1px solid rgba(192,57,43,${isHovered ? "0.4" : "0.15"})`,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#C0392B",
                transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
                marginBottom: 28,
                boxShadow: isHovered
                  ? "0 0 30px rgba(192,57,43,0.25), 0 0 60px rgba(192,57,43,0.1)"
                  : "0 0 20px rgba(192,57,43,0.08)",
                transform: isHovered ? "scale(1.1)" : "scale(1)",
              }}>
                <Icon size={32} strokeWidth={1.5} />
              </div>

              <h2 style={{
                color: "#fff", fontSize: 24, fontWeight: 700,
                marginBottom: 12, letterSpacing: "-0.5px",
              }}>
                {role.label}
              </h2>
              <p style={{ color: "rgba(255,255,255,0.35)", fontSize: 15, lineHeight: 1.7, marginBottom: 32 }}>
                {role.desc}
              </p>

              <div style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "14px 32px", borderRadius: 14,
                background: isHovered
                  ? "#C0392B"
                  : "rgba(192,57,43,0.1)",
                color: isHovered ? "#fff" : "rgba(192,57,43,0.8)",
                fontWeight: 600, fontSize: 15, transition: "all 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
                boxShadow: isHovered ? "0 8px 30px rgba(192,57,43,0.35)" : "none",
                border: isHovered ? "none" : "1px solid rgba(192,57,43,0.15)",
              }}>
                Accedi <ArrowRight size={18} style={{ transition: "transform 0.3s", transform: isHovered ? "translateX(4px)" : "translateX(0)" }} />
              </div>
            </button>
          );
        })}
      </div>

      <p className="animate-fade-in" style={{ color: "rgba(255,255,255,0.15)", fontSize: 11, marginTop: 64, position: "relative", zIndex: 1 }}>
        Mattone Rosso SRL — v2.0
      </p>
    </div>
  );
}
